/**
 * MyRefundsPanel — shows tickets eligible for refund because their event was
 * cancelled by the organizer or admin, and lets the user request an instant
 * Stripe refund. Promo (0 €) tickets never appear here.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Ticket, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MyRefundsPanel = ({ embedded = false }) => {
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/mymouv/my-refundable-tickets');
      setTickets(r.data?.tickets || []);
    } catch (e) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestRefund = async (ticket) => {
    if (!window.confirm(t('refunds.confirm', 'Demander le remboursement complet ? Le montant sera crédité sur votre carte sous 5 à 10 jours ouvrés.'))) return;
    setBusyId(ticket.id);
    try {
      await axios.post(`/api/mymouv/tickets/${ticket.id}/request-refund`);
      toast.success(t('refunds.success', 'Remboursement en cours — vous recevrez un e-mail de confirmation.'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('refunds.error', 'Erreur'));
    } finally {
      setBusyId(null);
    }
  };

  const paidCount = tickets.filter(t => t.status !== 'refunded').length;

  return (
    <div className={embedded ? '' : 'p-4'} data-testid="my-refunds-panel">
      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-6 text-white/60 text-sm">
          <Ticket className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {t('refunds.empty', 'Aucun billet éligible au remboursement pour le moment.')}
        </div>
      ) : (
        <>
          <p className="text-xs text-white/60 mb-3">
            {paidCount > 0
              ? t('refunds.eligibleCount', '{n} billet(s) éligible(s) au remboursement.').replace('{n}', paidCount)
              : t('refunds.allDone', 'Tous vos remboursements ont été traités.')}
          </p>
          <ul className="space-y-2" data-testid="my-refunds-list">
            {tickets.map(tk => (
              <li key={tk.id} className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={`refund-item-${tk.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white truncate">{tk.event_title}</div>
                    <div className="text-[11px] text-white/50">{tk.event?.event_date || tk.event_date}</div>
                    <div className="text-[11px] text-white/50 flex items-center gap-1 mt-1">
                      <AlertTriangle className="w-3 h-3 text-amber-400" />
                      {t('refunds.eventCancelled', 'Événement annulé')}
                      {tk.event?.cancel_reason && ` · ${tk.event.cancel_reason}`}
                    </div>
                    <div className="text-xs mt-1">
                      <span className="text-white/60">{t('refunds.amount', 'Montant')} :</span>{' '}
                      <span className="font-bold">{tk.price} {tk.currency}</span>
                      <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-mono">{tk.ticket_number}</span>
                    </div>
                  </div>
                  {tk.status === 'refunded' ? (
                    <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs flex items-center gap-1 flex-shrink-0" data-testid={`refund-done-${tk.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {t('refunds.done', 'Remboursé')}
                    </span>
                  ) : (
                    <button
                      onClick={() => requestRefund(tk)}
                      disabled={busyId === tk.id}
                      className="px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1 flex-shrink-0"
                      data-testid={`refund-request-btn-${tk.id}`}
                    >
                      {busyId === tk.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      {t('refunds.requestBtn', 'Demander le remboursement')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default MyRefundsPanel;
